import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useCallback, useMemo, useState } from 'react';

import chart from '~/assets/icons/chart.svg';
import { ChartPopover } from '~/pages/Company/components/Inference/components/ChartPopover';
import { ExitZone } from '~/pages/Company/components/Inference/components/ExitZone';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

interface Props {
  kpcaRo: number;
  kpcaFi: number;
  data: GetCompanyInferenceResponse['result']['data']['exit_score'];
  scatterChartData: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['exit_score'];
}

const Component: React.FC<Props> = ({ kpcaRo, kpcaFi, data, scatterChartData }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChartButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleChartPopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const bars = useMemo<
    GetCompanyInferenceResponse['result']['data']['exit_score']['graph_mode'][]
  >(() => {
    return ['below_average', 'margin_of_error', 'above_average'];
  }, []);

  const renderBar = useCallback(
    (
      bar: GetCompanyInferenceResponse['result']['data']['exit_score']['graph_mode'],
      index: number,
    ) => {
      const names: Record<
        GetCompanyInferenceResponse['result']['data']['exit_score']['graph_mode'],
        string
      > = {
        below_average: 'Below Average',
        margin_of_error: 'Margin of Error',
        above_average: 'Above Average',
      };

      return (
        <div
          className={clsx(styles.chart__bar, {
            [styles['chart__bar--last']]: index === bars.length - 1,
            [styles['chart__bar--below']]: bar === data.graph_mode && bar === 'below_average',
            [styles['chart__bar--margin']]: bar === data.graph_mode && bar === 'margin_of_error',
            [styles['chart__bar--above']]: bar === data.graph_mode && bar === 'above_average',
          })}
          key={bar}
        >
          {bar === data.graph_mode && (
            <>
              <Typography
                color="var(--text-3-color)"
                fontWeight="400"
                lineHeight="22px"
                variant="h6"
              >
                Exit likelihood
              </Typography>
              <Typography
                align="center"
                color="var(--text-3-color)"
                fontWeight="700"
                lineHeight="22px"
                mb="3px"
                variant="body1"
              >
                {names[bar]} {data.exit_prob_vs_av_x}
              </Typography>
            </>
          )}
          <Typography align="center" color="var(--gray-2-color)" variant="subtitle2">
            {names[bar]}
          </Typography>
        </div>
      );
    },
    [bars.length, data.exit_prob_vs_av_x, data.graph_mode],
  );

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-3-color)" fontWeight="500" variant="subtitle1">
          Exit Likilihood
        </Typography>
        <button type="button" onClick={handleChartButtonClick}>
          <img alt="chart" src={chart} />
        </button>
        <ChartPopover anchorEl={anchorEl} onClose={handleChartPopoverClose}>
          <ExitZone
            className={styles['container__scatter-chart']}
            data={scatterChartData}
            kpcaFi={kpcaFi}
            kpcaRo={kpcaRo}
          />
        </ChartPopover>
      </div>
      <div className={styles.chart}>
        <div className={styles.chart__bars}>{bars.map(renderBar)}</div>
        <div className={styles['chart__x-axis-line']} />
      </div>
    </div>
  );
};

export const ExitLikilihood = memo(Component);
